// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ProductImageBatch } from "@/api/hooks/use-product-images";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

// The backend keeps at most this many failures per batch; shown in the popover hint.
const MAX_ERRORS = 100;

// Same vocabulary as the shared ProgressBar: done on the primary colour, the muted track for the rest,
// and the destructive colour only for failures.
const DONE = "bg-primary";
const FAILED = "bg-destructive";

/**
 * One stage of the pipeline: the files that passed it on the primary colour, the ones that failed at
 * it in red, both as a share of the mapped files.
 */
function StageBar({
  label,
  count,
  failed,
  mapped,
  failedLabel,
}: {
  label: string;
  count: number;
  failed: number;
  mapped: number;
  failedLabel: string;
}) {
  const share = (value: number) => (mapped > 0 ? (Math.max(0, value) / mapped) * 100 : 0);
  const percent = Math.round(share(count));
  return (
    <div className="grid grid-cols-[6rem_1fr_auto] items-center gap-2 text-xs tabular-nums">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full", DONE)} style={{ width: `${share(count)}%` }} />
        <div className={cn("h-full", FAILED)} style={{ width: `${share(failed)}%` }} />
      </div>
      <span className="min-w-[6.5rem] text-right text-muted-foreground">
        {percent}% ({count}/{mapped}){failed > 0 && <span className="ml-1 text-destructive">{failedLabel}</span>}
      </span>
    </div>
  );
}

/**
 * Progress of one batch through the pipeline. On top the files that reached the end: resized on the
 * primary colour, failed on the destructive one, the muted track for whatever is still in flight.
 * Unfolded, one bar per stage shows how many files have passed it so far. The fold state is owned by
 * the page, so a poll that re-renders the list does not close it.
 */
export function BatchProgress({
  batch,
  open,
  onOpenChange,
}: {
  batch: ProductImageBatch;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("back-office");
  const { mapped, received, uploaded, queued, processed, failed, queueFailed, processFailed } = batch.summary;
  // `failed` is every failure before the resize; the part Kafka rejected belongs to the queue stage
  const uploadFailed = failed - queueFailed;
  const failedTotal = failed + processFailed;
  const failedLabel = (count: number) => t("productImages.list.failed", { count });
  const done = processed + failedTotal;
  const percent = mapped > 0 ? Math.round((done / mapped) * 100) : 100;
  const width = (count: number) => `${mapped > 0 ? (Math.max(0, count) / mapped) * 100 : 0}%`;

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="flex min-w-[16rem] flex-col gap-1">
      <div className="flex items-center gap-2 text-xs leading-none tabular-nums">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded leading-none hover:bg-muted"
            aria-label={t("productImages.list.stagesToggle")}
          >
            <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")} />
            <span className={cn("font-medium", failedTotal > 0 ? "text-destructive" : "text-foreground")}>
              {percent}%
            </span>
          </button>
        </CollapsibleTrigger>
        <span className="text-muted-foreground">{t("productImages.list.progressDone", { done, mapped })}</span>
        {failedTotal > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="ml-auto text-destructive underline-offset-2 hover:underline">
                {t("productImages.list.failed", { count: failedTotal })}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[32rem] p-0">
              <div className="border-b px-3 py-2">
                <p className="text-sm font-medium">{t("productImages.list.errorsTitle")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("productImages.list.errorsHint", { max: MAX_ERRORS })}
                </p>
              </div>
              <div className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("productImages.columns.fileName")}</TableHead>
                      <TableHead className="w-28">{t("productImages.columns.validation")}</TableHead>
                      <TableHead>{t("productImages.columns.detail")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batch.errors.map((error) => (
                      <TableRow key={`${error.stage}:${error.fileName}`}>
                        <TableCell className="font-medium">{error.fileName}</TableCell>
                        <TableCell>{t(`productImages.list.errorStage.${error.stage}`, error.stage)}</TableCell>
                        <TableCell className="whitespace-normal text-muted-foreground">{error.detail}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        className="flex h-2 w-full overflow-hidden rounded-full bg-muted"
      >
        <div className={cn("h-full", DONE)} style={{ width: width(processed) }} />
        <div className={cn("h-full", FAILED)} style={{ width: width(failedTotal) }} />
      </div>
      <CollapsibleContent className="flex flex-col gap-1 pt-1">
        <StageBar
          label={t("productImages.summary.received")}
          count={received}
          failed={0}
          mapped={mapped}
          failedLabel=""
        />
        <StageBar
          label={t("productImages.summary.uploaded")}
          count={uploaded}
          failed={uploadFailed}
          mapped={mapped}
          failedLabel={failedLabel(uploadFailed)}
        />
        <StageBar
          label={t("productImages.summary.queued")}
          count={queued}
          failed={queueFailed}
          mapped={mapped}
          failedLabel={failedLabel(queueFailed)}
        />
        <StageBar
          label={t("productImages.summary.processed")}
          count={processed}
          failed={processFailed}
          mapped={mapped}
          failedLabel={failedLabel(processFailed)}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}
